/**
 * The machine block every benchmark report starts with (P2.0-T4, the author's request of 2026-10-03), collected from
 * what the browser exposes so nobody types specs: the browser and its version, the platform, the processor's logical
 * cores and the device memory, the GPU's vendor and renderer from WebGL and WebGPU, the power preference the WebGL
 * context was made with, the WebGL version and largest
 * texture, Phaser's version, the screen, the pixel ratio, the canvas and the window, the measured refresh rate, the
 * battery's charge and whether it is charging, and the run's date, build, world and route. Every field is optional:
 * one the browser does not expose reads "unavailable", one it hides reads "masked", and nothing here ever throws or
 * leaves the page.
 */

/**
 * What a field reads when the browser does not expose it.
 */
export const UNAVAILABLE = "unavailable";

/**
 * What a GPU field reads when the browser hides it.
 */
export const MASKED = "masked";

/**
 * One field of the block: its label and its value as text.
 */
export interface MachineField {
  readonly key: string;
  readonly value: string;
}

/**
 * The browser's client hints, where it has them (Chromium).
 */
interface UserAgentData {
  readonly platform?: string;
  getHighEntropyValues(hints: string[]): Promise<Record<string, unknown>>;
}

/**
 * The battery, where the browser exposes it (Chromium).
 */
interface BatteryState {
  readonly charging: boolean;
  readonly level: number;
}

/**
 * The parts of `navigator` some browsers have and the DOM types do not list.
 */
interface ExtendedNavigator {
  readonly userAgentData?: UserAgentData;
  readonly deviceMemory?: number;
  readonly gpu?: { requestAdapter(options?: { powerPreference?: "high-performance" | "low-power" }): Promise<{ readonly info?: Record<string, unknown> } | null> };
  getBattery?(): Promise<BatteryState>;
}

/**
 * What the page hands the collection: the WebGL context Phaser draws with, the canvas, Phaser's version, the
 * run's metadata, and the render resolution in force in words (ADR-093 decision 10), such as "Half (divisor 2)".
 */
export interface MachineSources {
  readonly gl: WebGLRenderingContext | WebGL2RenderingContext | null;
  readonly canvas: HTMLCanvasElement | null;
  readonly phaserVersion: string;
  readonly build: string;
  readonly world: string;
  readonly map: string;
  readonly route: number;
  readonly label: string;
  readonly render: string;
}

/**
 * Runs a reading and turns any failure, or an empty answer, into "unavailable".
 *
 * @param read The reading.
 * @returns Its text, or "unavailable".
 */
async function safely(read: () => Promise<string | null | undefined> | string | null | undefined): Promise<string> {
  try {
    const value = await read();
    return value === null || value === undefined || value === "" ? UNAVAILABLE : value;
  } catch {
    return UNAVAILABLE;
  }
}

/**
 * The client hints' high-entropy values, or null where the browser has none.
 *
 * @param nav The navigator.
 * @returns The values, or null.
 */
async function highEntropy(nav: ExtendedNavigator): Promise<Record<string, unknown> | null> {
  try {
    return (await nav.userAgentData?.getHighEntropyValues(["fullVersionList", "platformVersion", "architecture", "model", "bitness"])) ?? null;
  } catch {
    return null;
  }
}

/**
 * The browsers and versions the client hints name, as "Brand version" joined, leaving out the hints' placeholder brand.
 *
 * @param hints The high-entropy values.
 * @returns The list, or null.
 */
function versionList(hints: Record<string, unknown> | null): string | null {
  const list = hints?.["fullVersionList"];
  if (!Array.isArray(list)) {
    return null;
  }
  return list
    .flatMap((entry: unknown) => {
      const brand = (entry as { brand?: unknown }).brand;
      const version = (entry as { version?: unknown }).version;
      return typeof brand === "string" && typeof version === "string" && !brand.includes("Brand") ? [`${brand} ${version}`] : [];
    })
    .join(", ");
}

/**
 * A text field of the client hints, or null.
 *
 * @param hints The high-entropy values.
 * @param key The hint.
 * @returns The text, or null.
 */
function hint(hints: Record<string, unknown> | null, key: string): string | null {
  const value = hints?.[key];
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * The GPU's vendor and renderer as WebGL unmasks them, "masked" when the browser withholds the extension.
 *
 * @param gl The context.
 * @returns The vendor and the renderer.
 */
function webglGpu(gl: MachineSources["gl"]): { vendor: string; renderer: string } {
  if (gl === null) {
    return { vendor: UNAVAILABLE, renderer: UNAVAILABLE };
  }
  try {
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    if (info === null) {
      return { vendor: MASKED, renderer: MASKED };
    }
    const vendor = gl.getParameter(info.UNMASKED_VENDOR_WEBGL) as unknown;
    const renderer = gl.getParameter(info.UNMASKED_RENDERER_WEBGL) as unknown;
    return { vendor: typeof vendor === "string" && vendor !== "" ? vendor : MASKED, renderer: typeof renderer === "string" && renderer !== "" ? renderer : MASKED };
  } catch {
    return { vendor: UNAVAILABLE, renderer: UNAVAILABLE };
  }
}

/**
 * The power preference the WebGL context was made with, as the context reports it: what the page asked for, which a
 * browser or the system may still overrule, so the renderer line beside it says which GPU drew.
 *
 * @param gl The context.
 * @returns "high-performance", "low-power" or "default", or null when the context does not say.
 */
function contextPowerPreference(gl: MachineSources["gl"]): WebGLPowerPreference | null {
  try {
    return gl?.getContextAttributes()?.powerPreference ?? null;
  } catch {
    return null;
  }
}

/**
 * The WebGPU adapter's description, or "unavailable" without WebGPU, or "masked" when it describes nothing. The
 * adapter is asked for with the WebGL context's power preference, so the line describes the GPU the same request
 * gives; Phaser draws with WebGL only, so the line is a second reading and never the renderer.
 *
 * @param nav The navigator.
 * @param preference The WebGL context's power preference, or null.
 * @returns The text.
 */
async function webgpuAdapter(nav: ExtendedNavigator, preference: WebGLPowerPreference | null): Promise<string> {
  if (nav.gpu === undefined) {
    return UNAVAILABLE;
  }
  return safely(async () => {
    const adapter = await nav.gpu?.requestAdapter(preference === null || preference === "default" ? undefined : { powerPreference: preference });
    if (adapter === null || adapter === undefined) {
      return null;
    }
    const info = adapter.info ?? {};
    const parts = ["vendor", "architecture", "device", "description"].flatMap((key) => {
      const value = info[key];
      return typeof value === "string" && value !== "" ? [`${key} ${value}`] : [];
    });
    return parts.length === 0 ? MASKED : parts.join(", ");
  });
}

/**
 * The display's refresh rate measured from animation frame intervals: the median of 60 intervals, in Hz.
 *
 * @param frames How many intervals to measure.
 * @returns The rate as text, or "unavailable".
 */
export function measureRefresh(frames = 60): Promise<string> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== "function") {
      resolve(UNAVAILABLE);
      return;
    }
    const intervals: number[] = [];
    let last = -1;
    /**
     * Records one interval and asks for the next frame until enough are measured.
     *
     * @param now The frame's time.
     */
    const tick = (now: number): void => {
      if (last >= 0) {
        intervals.push(now - last);
      }
      last = now;
      if (intervals.length < frames) {
        requestAnimationFrame(tick);
        return;
      }
      const median = [...intervals].sort((a, b) => a - b)[Math.floor(intervals.length / 2)] ?? 0;
      resolve(median > 0 ? `${Math.round(1000 / median)} Hz (median of ${intervals.length} frame intervals)` : UNAVAILABLE);
    };
    requestAnimationFrame(tick);
  });
}

/**
 * The browser zoom, where it can be told: the window's outer width over its inner width, a rough reading that holds
 * only with no side panel open, so it is labelled an estimate.
 *
 * @returns The text.
 */
function zoomEstimate(): string {
  if (window.outerWidth <= 0 || window.innerWidth <= 0) {
    return UNAVAILABLE;
  }
  return `about ${Math.round((window.outerWidth / window.innerWidth) * 100)}% (outer over inner width, an estimate)`;
}

/**
 * Collects the machine block.
 *
 * @param sources The page's context, canvas, Phaser version and the run's metadata.
 * @param refresh The measured refresh rate, measured before the run.
 * @returns The fields, in the order the report prints them.
 */
export async function collectMachine(sources: MachineSources, refresh: string): Promise<MachineField[]> {
  const nav = navigator as Navigator & ExtendedNavigator;
  const hints = await highEntropy(nav);
  const gpu = webglGpu(sources.gl);
  const preference = contextPowerPreference(sources.gl);
  const battery = await safely(async () => {
    if (nav.getBattery === undefined) {
      return null;
    }
    const state = await nav.getBattery();
    return `${state.charging ? "charging (plugged in)" : "not charging (on battery)"}, ${Math.round(state.level * 100)}%`;
  });
  const gl = sources.gl;
  const fields: [string, string | Promise<string>][] = [
    ["Label", sources.label === "" ? UNAVAILABLE : sources.label],
    ["Date", new Date().toISOString()],
    ["Build", sources.build],
    ["World", `${sources.world}/${sources.map}`],
    ["Bench route", `v${sources.route}`],
    ["Browser", safely(() => versionList(hints))],
    ["User agent", safely(() => navigator.userAgent)],
    ["Platform", safely(() => hint(hints, "platform") ?? nav.userAgentData?.platform ?? navigator.platform)],
    ["Platform version", safely(() => hint(hints, "platformVersion"))],
    ["Architecture", safely(() => {
      const architecture = hint(hints, "architecture");
      const bitness = hint(hints, "bitness");
      return architecture === null ? null : `${architecture}${bitness === null ? "" : ` ${bitness}-bit`}`;
    })],
    ["Model", safely(() => hint(hints, "model"))],
    ["CPU logical cores", safely(() => (navigator.hardwareConcurrency > 0 ? String(navigator.hardwareConcurrency) : null))],
    ["Device memory", safely(() => (nav.deviceMemory === undefined ? null : `${nav.deviceMemory} GB (as the browser reports it; Chromium rounds it and may cap it)`))],
    ["GPU vendor", gpu.vendor],
    ["GPU renderer", gpu.renderer],
    ["GPU power preference", preference === null ? UNAVAILABLE : `${preference} (what the page asked for; the browser or the system may give another GPU)`],
    ["WebGPU adapter", webgpuAdapter(nav, preference)],
    ["WebGL version", safely(() => (gl === null ? null : String(gl.getParameter(gl.VERSION))))],
    ["Max texture size", safely(() => (gl === null ? null : String(gl.getParameter(gl.MAX_TEXTURE_SIZE))))],
    ["Phaser", sources.phaserVersion === "" ? UNAVAILABLE : sources.phaserVersion],
    ["Screen", safely(() => `${screen.width} x ${screen.height}`)],
    ["Device pixel ratio", safely(() => String(window.devicePixelRatio))],
    ["Render resolution", sources.render === "" ? UNAVAILABLE : sources.render],
    ["Canvas backing size", safely(() => (sources.canvas === null ? null : `${sources.canvas.width} x ${sources.canvas.height}`))],
    ["Window size", safely(() => `${window.innerWidth} x ${window.innerHeight}`)],
    ["Browser zoom", safely(() => zoomEstimate())],
    ["Refresh rate", refresh],
    ["Power", battery],
  ];
  const values = await Promise.all(fields.map(([, value]) => value));
  return fields.map(([key], index) => ({ key, value: values[index] as string }));
}
